/**
 * Tidying the roster — FR-ADMIN-1, BR-DM-5.
 *
 * A guest record is what a mis-typed name at courtside produces, and until now
 * the admin roster screen filtered guests out entirely — so the club could see
 * that three players called Pavan existed only by scrolling an attendance
 * sheet. Three operations, and the difference between them is which one keeps
 * the games:
 *
 *   Delete    only when nothing is attached. A guest who has played is never
 *             deleted; their results would be left belonging to nobody.
 *   Convert   the same row relabelled. Every game already points at this id,
 *             so nothing moves and no statistic can end up split in two.
 *   Merge     for when the guest is somebody the club already has. Every game,
 *             result and attendance row moves onto the surviving player, so
 *             the two sets of figures become one.
 *
 * Duplicate names are shown, never acted on. CLAUDE.md forbids auto-merging
 * suspected duplicate games and the same reasoning holds for people: the app
 * can see that two rows share a name, and only a member knows whether they are
 * the same person.
 */

import { useCallback, useEffect, useState } from "react";
import { api, type RosterPlayer } from "../api.js";

type Busy = { id: string; what: "delete" | "convert" | "merge" } | null;

export function GuestAdmin(): React.JSX.Element {
  const [players, setPlayers] = useState<RosterPlayer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);

  const load = useCallback(() => {
    api
      .roster(true)
      .then(setPlayers)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Could not load the roster."),
      );
  }, []);

  useEffect(load, [load]);

  const guests = (players ?? []).filter((p) => p.isGuest);
  const members = (players ?? []).filter((p) => !p.isGuest);

  if (guests.length === 0) {
    return (
      <section className="block">
        <h3 className="card-head">Guests</h3>
        <p>
          No guest records. A walk-in added at courtside appears here until they
          are made a member or removed.
        </p>
      </section>
    );
  }

  function run(what: NonNullable<Busy>["what"], id: string, action: Promise<unknown>): void {
    setBusy({ id, what });
    setError(null);
    setNote(null);
    action
      .then(() => {
        setOpen(null);
        load();
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "That did not work."))
      .finally(() => setBusy(null));
  }

  return (
    <section className="block">
      <h3 className="card-head">
        Guests
        <span className="muted small">{guests.length}</span>
      </h3>

      {error ? <p className="error">{error}</p> : null}
      {note ? <p className="ok-note">{note}</p> : null}

      {guests.map((guest) => {
        /* Shown, never acted on: the club decides who is the same person. */
        const sameName = members.filter(
          (m) => m.displayName.toLowerCase() === guest.displayName.toLowerCase(),
        );
        const isOpen = open === guest.id;

        return (
          <div key={guest.id} className="rotrow guestrow">
            <span className="who">
              {guest.displayName}
              <span className="guest-flag">guest</span>
              {sameName.length > 0 ? (
                <span className="muted small"> · a member has this name too</span>
              ) : null}
            </span>
            <button className="mini" onClick={() => setOpen(isOpen ? null : guest.id)}>
              {isOpen ? "Close" : "Sort out"}
            </button>

            {isOpen ? (
              <div className="guest-actions">
                <button
                  className="chip"
                  disabled={busy !== null}
                  onClick={() =>
                    run("convert", guest.id, api.convertGuest(guest.id))
                  }
                >
                  {busy?.id === guest.id && busy.what === "convert"
                    ? "Converting…"
                    : "Make them a member"}
                </button>
                <p className="muted small">
                  Keeps every game they have played, on the same record.
                </p>

                {sameName.map((m) => (
                  <button
                    key={m.id}
                    className="chip"
                    disabled={busy !== null}
                    onClick={() => {
                      // Irreversible, so it is asked once and named plainly.
                      if (
                        !window.confirm(
                          `Merge the guest "${guest.displayName}" into the member ${m.displayName}?\n\n` +
                            `Every game the guest played moves onto ${m.displayName}. This cannot be undone.`,
                        )
                      ) {
                        return;
                      }
                      run(
                        "merge",
                        guest.id,
                        api
                          .mergePlayers(m.id, guest.id, `Guest record for ${m.displayName}`)
                          .then((r) => {
                            setNote(
                              `Merged. ${String(r.gamesReassigned)} ${
                                r.gamesReassigned === 1 ? "game" : "games"
                              } and ${String(r.attendanceReassigned)} attendance ${
                                r.attendanceReassigned === 1 ? "record" : "records"
                              } moved to ${m.displayName}` +
                                (r.attendanceDeduplicated > 0
                                  ? `, and ${String(r.attendanceDeduplicated)} duplicate attendance ${
                                      r.attendanceDeduplicated === 1 ? "row was" : "rows were"
                                    } folded together.`
                                  : "."),
                            );
                          }),
                      );
                    }}
                  >
                    They are {m.displayName}
                  </button>
                ))}

                <button
                  className="chip chip-warn"
                  disabled={busy !== null}
                  onClick={() => {
                    const reason = window.prompt(
                      `Delete the guest record "${guest.displayName}"?\n\nSay why:`,
                      "Added by mistake",
                    );
                    if (!reason) return;
                    run("delete", guest.id, api.deleteGuest(guest.id, reason));
                  }}
                >
                  {busy?.id === guest.id && busy.what === "delete" ? "Deleting…" : "Delete"}
                </button>
                <p className="muted small">
                  Only possible if they have played no games and are on no
                  session. Otherwise merge them into the right player instead.
                </p>
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
